import { useMemo } from 'react';
import { UserProfile } from '../types';

export interface ResolvedCompanyProfile {
  businessName: string;
  ownerName: string;
  document: string;
  pixKey: string;
  pixKeyType: string;
  logoUrl: string;
  niche: string;
  hasCustomBusinessName: boolean;
}

export const BANNED_LEGACY_NAMES = new Set([
  'sorveteria do jairo pierin',
  'jairo pierin',
  'empório & granel r-max',
  'emporio & granel r-max',
  'empório & granel',
  'emporio & granel',
  'gerente empório',
  'gerente emporio',
  'gerente',
  'operador',
  'visitante',
  'usuário r max',
  'usuário r-max',
  'usuario r max',
  'usuario r-max',
  'r-max (modo teste)'
]);

export function isBannedCompanyName(name?: string | null): boolean {
  if (!name) return false;
  return BANNED_LEGACY_NAMES.has(name.trim().toLowerCase());
}

/**
 * Higieniza e resolve o Nome Comercial de forma universal e à prova de falhas.
 * Prioridade:
 * 1. user.businessName (Cadastro oficial em Marca e Empresa)
 * 2. user.ownerName (Nome do responsável, desde que não seja papel de operador ou mock)
 * 3. user.fullName (Nome do usuário, desde que não seja papel ou mock)
 * 4. defaultFallback (Termo neutro padrão, ex: 'Minha Empresa' ou 'Minha Sorveteria')
 */
export function resolveCompanyName(
  user?: Partial<UserProfile> | null,
  defaultFallback: string = 'Minha Empresa'
): string {
  if (!user) return defaultFallback;

  const rawBusiness = user.businessName?.trim();
  if (rawBusiness && !isBannedCompanyName(rawBusiness)) {
    return rawBusiness;
  }

  const rawOwner = user.ownerName?.trim();
  if (rawOwner && !isBannedCompanyName(rawOwner)) {
    return rawOwner;
  }

  const rawFullName = user.fullName?.trim();
  if (rawFullName && !isBannedCompanyName(rawFullName)) {
    return rawFullName;
  }

  return defaultFallback;
}

/**
 * Higieniza e resolve a URL ou Base64 do logotipo da empresa de forma universal.
 * Prioridade:
 * 1. storefront?.logoUrl (Vitrine pública)
 * 2. user?.logoUrl (URL pública no Supabase Storage)
 * 3. user?.logoBase64 (Base64 compactado local)
 */
export function resolveCompanyLogo(
  user?: Partial<UserProfile> | null,
  storefront?: { logoUrl?: string | null } | null
): string {
  const sfLogo = storefront?.logoUrl?.trim();
  if (sfLogo) return sfLogo;
  const userUrl = user?.logoUrl?.trim();
  if (userUrl) return userUrl;
  const userBase64 = user?.logoBase64?.trim();
  if (userBase64) return userBase64;
  return '';
}

/**
 * Resolve o objeto completo de identidade comercial da empresa.
 */
export function resolveCompanyProfile(
  user?: Partial<UserProfile> | null,
  defaultFallback: string = 'Minha Empresa',
  storefront?: { logoUrl?: string | null } | null
): ResolvedCompanyProfile {
  const businessName = resolveCompanyName(user, defaultFallback);
  const rawBusiness = user?.businessName?.trim();
  const hasCustomBusinessName = Boolean(
    rawBusiness && !isBannedCompanyName(rawBusiness)
  );

  const rawOwner = user?.ownerName?.trim() || user?.fullName?.trim() || '';
  const ownerName = (rawOwner && !isBannedCompanyName(rawOwner)) ? rawOwner : 'Operador';
  const document = user?.document?.trim() || '';
  const pixKey = user?.pixKey?.trim() || '';
  const pixKeyType = user?.pixKeyType || 'aleatoria';
  const logoUrl = resolveCompanyLogo(user, storefront);
  const niche = user?.niche || '';

  return {
    businessName,
    ownerName,
    document,
    pixKey,
    pixKeyType,
    logoUrl,
    niche,
    hasCustomBusinessName
  };
}

/**
 * Hook do React para consumir a identidade comercial unificada em qualquer componente.
 */
export function useCompanyProfile(
  user?: Partial<UserProfile> | null,
  options?: { defaultFallback?: string }
): ResolvedCompanyProfile {
  const fallback = options?.defaultFallback ?? 'Minha Empresa';

  return useMemo(() => {
    return resolveCompanyProfile(user, fallback);
  }, [
    user?.businessName,
    user?.ownerName,
    user?.fullName,
    user?.document,
    user?.pixKey,
    user?.pixKeyType,
    user?.logoUrl,
    user?.logoBase64,
    user?.niche,
    fallback
  ]);
}
